'use client';

import Conditional from '@next-app/components/Conditional';
import LLButton from '@next-app/components/elements/LLButton/LLButton';
import LLInput from '@next-app/components/elements/LLInput/LLInput';
import LLPopover from '@next-app/components/elements/LLPopover/LLPopover';
import {
  selectDynSess,
  selectIsFurniture,
  selectIsGuestUser,
  selectLlmAPIVersion,
} from '@next-app/lib/features/InitSelectors';
import {
  selectIsPureBopisOrder,
  selectPaymentDetailInfo,
  selectPaymentsInfo,
} from '@next-app/lib/features/checkout/CheckoutSelector';
import {
  applyGiftCardReq,
  handleCheckoutServerErrors,
  sendOnBlurDTMEvent,
  triggerCheckoutFormErrorEvent,
  validateFields,
} from '@next-app/utils/CheckoutHelper';
import { ChangeEvent, FormEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StorePickupInfoFormFields } from './BopisStorePickupInfoFormFields';
import {
  CHECKOUT_VALIDATION_MESSAGES,
  FIELD_INVALID_CHARACTERS,
  FIELD_VALIDATION_RULES,
} from '@next-app/constants/CheckoutConstants';
import {
  formatPhoneNumber,
  getUnformattedNumber,
} from '@next-app/utils/form-validation';
import {
  setIsSubmitOrder,
  setLoadCheckoutDetails,
} from '@next-app/lib/features/checkout/CheckoutSlice';

interface IBopisStorePickupInfoFormState {
  [StorePickupInfoFormFields.FIRST_NAME]: string;
  [StorePickupInfoFormFields.LAST_NAME]: string;
  [StorePickupInfoFormFields.PHONE_NO]: string;
}

const BopisStorePickupInfo = () => {
  const dispatch = useDispatch();

  const dynSessConf = useSelector(selectDynSess);
  const isUserGuest = useSelector(selectIsGuestUser);
  const isPureBopis = useSelector(selectIsPureBopisOrder);
  const paymentDetailsInfo = useSelector(selectPaymentDetailInfo);
  const paymentInfo = useSelector(selectPaymentsInfo);
  const isFurniture = useSelector(selectIsFurniture);
  const llmAPIVersion = useSelector(selectLlmAPIVersion);

  const [storePickupInfoFormState, setStorePickupInfoFormState] =
    useState<IBopisStorePickupInfoFormState>({
      [StorePickupInfoFormFields.FIRST_NAME]: '',
      [StorePickupInfoFormFields.LAST_NAME]: '',
      [StorePickupInfoFormFields.PHONE_NO]: '',
    });

  const [storePickupInfoFormErrors, setStorePickupInfoFormErrors] =
    useState<Record<string, string>>();

  const updateBopisStorePickupInfoFormState = (
    newState: Partial<IBopisStorePickupInfoFormState>,
  ) => {
    setStorePickupInfoFormState({
      ...storePickupInfoFormState,
      ...newState,
    });
  };

  const handleFormChange = (
    event: ChangeEvent,
    fieldType:
      | StorePickupInfoFormFields.FIRST_NAME
      | StorePickupInfoFormFields.LAST_NAME
      | StorePickupInfoFormFields.PHONE_NO,
  ) => {
    const value = (event.target as HTMLInputElement).value;
    setStorePickupInfoFormErrors((prev) => ({ ...prev, [fieldType]: '' }));
    updateBopisStorePickupInfoFormState({
      [fieldType]:
        fieldType == StorePickupInfoFormFields.PHONE_NO
          ? formatPhoneNumber(value)
          : fieldType == StorePickupInfoFormFields.FIRST_NAME
            ? value.replace(FIELD_INVALID_CHARACTERS.FIRST_NAME, '')
            : fieldType == StorePickupInfoFormFields.LAST_NAME
              ? value.replace(FIELD_INVALID_CHARACTERS.LAST_NAME, '')
              : value,
    });
  };

  const validateBopisStorePickupInfoField = (
    name: string,
    value: string,
  ): string => {
    let errorMessage = '';
    switch (name) {
      case StorePickupInfoFormFields.FIRST_NAME: {
        if (!value)
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.FIRST_NAME_REQUIRED;
        else if (!FIELD_VALIDATION_RULES.FIRST_NAME.test(value))
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.FIRST_NAME_INVALID;
        break;
      }
      case StorePickupInfoFormFields.LAST_NAME: {
        if (!value)
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.LAST_NAME_REQUIRED;
        else if (!FIELD_VALIDATION_RULES.LAST_NAME.test(value))
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.LAST_NAME_INVALID;
        break;
      }
      case StorePickupInfoFormFields.PHONE_NO: {
        if (!value)
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.PHONE_NO_IS_REQUIRED;
        if (
          !FIELD_VALIDATION_RULES.US_CA_PHONE.test(getUnformattedNumber(value))
        )
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.PHONE_NO_INVALID;
        break;
      }
    }
    return errorMessage;
  };

  const addStorePickupInfo = async (e: FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    const isFormValid = validateFields(
      storePickupInfoFormState,
      validateBopisStorePickupInfoField,
      setStorePickupInfoFormErrors,
    );

    if (isFormValid) {
      setStorePickupInfoFormErrors({});
      // handle API interaction here
      const updateStorePickupInfoRequest = {
        firstName:
          storePickupInfoFormState[StorePickupInfoFormFields.FIRST_NAME],
        lastName: storePickupInfoFormState[StorePickupInfoFormFields.LAST_NAME],
        phoneNumber: getUnformattedNumber(
          storePickupInfoFormState[
            StorePickupInfoFormFields.PHONE_NO
          ] as string,
        ),
        updateStorePickupInfo: true,
        applyGiftCard: false,
        _dynSessConf: dynSessConf,
        howMany: -1,
        start: 0,
      };
      const updateStorePickupInfoResponse = await applyGiftCardReq(
        updateStorePickupInfoRequest,
        isFurniture,
      );

      if (updateStorePickupInfoResponse?.checkoutResponse) {
        dispatch(
          setLoadCheckoutDetails(
            updateStorePickupInfoResponse?.checkoutResponse,
          ),
        );
        dispatch(setIsSubmitOrder(true));
      }

      if (updateStorePickupInfoResponse.formError) {
        triggerCheckoutFormErrorEvent({
          step: 'delivery',
          llmAPIVersion,
          isFurniture,
          errors: updateStorePickupInfoResponse?.formExceptions,
        });
        handleCheckoutServerErrors(
          updateStorePickupInfoResponse.formExceptions,
          setStorePickupInfoFormErrors,
        );
      }
    }
  };

  const onBlurHandler = (
    key: string,
    e: React.FocusEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    const errorMessage = validateBopisStorePickupInfoField(key, value);
    if (errorMessage) {
      setStorePickupInfoFormErrors((prev) => ({
        ...prev,
        [key]: errorMessage,
      }));
    }
    sendOnBlurDTMEvent({ key, errorMessage, value });
  };

  return (
    <Conditional
      if={
        isUserGuest &&
        isPureBopis &&
        !paymentDetailsInfo.additionalPaymentRequired &&
        (!paymentInfo?.address?.firstName ||
          !paymentInfo?.address?.lastName ||
          !paymentInfo?.address?.phoneNumber)
      }
    >
      <>
        <div className="mt-ll-3">
          <div>
            <span className="fs-base fw-light lh-sm">Store Pickup Info</span>
          </div>
          <div className="mt-ll-3">
            <p className="fs-xs" style={{ lineHeight: '16px' }}>
              A name and telephone number are required for the person picking up
              the order.
            </p>
          </div>
        </div>
        <form
          className="needs-validation mt-ll-3"
          onSubmit={addStorePickupInfo}
        >
          <div className="row g-ll-3">
            <div className="col-12 col-md-4">
              <LLInput
                testid="firstNameStorePickupInfo"
                type="text"
                placeholder="First Name"
                id="firstNameStorePickupInfo"
                value={
                  storePickupInfoFormState[StorePickupInfoFormFields.FIRST_NAME]
                }
                errorMsg={
                  storePickupInfoFormErrors?.[
                    StorePickupInfoFormFields.FIRST_NAME
                  ]
                }
                onChange={(e) =>
                  handleFormChange(e, StorePickupInfoFormFields.FIRST_NAME)
                }
                onBlur={(e) =>
                  onBlurHandler(StorePickupInfoFormFields.FIRST_NAME, e)
                }
                maxLength={40}
              />
            </div>
            <div className="col-12 col-md-4">
              <LLInput
                testid="lastNameStorePickupInfo"
                type="text"
                placeholder="Last Name"
                id="lastNameStorePickupInfo"
                value={
                  storePickupInfoFormState[StorePickupInfoFormFields.LAST_NAME]
                }
                errorMsg={
                  storePickupInfoFormErrors?.[
                    StorePickupInfoFormFields.LAST_NAME
                  ]
                }
                onChange={(e) =>
                  handleFormChange(e, StorePickupInfoFormFields.LAST_NAME)
                }
                onBlur={(e) =>
                  onBlurHandler(StorePickupInfoFormFields.LAST_NAME, e)
                }
                maxLength={40}
              />
            </div>
            <div className="col-12 col-md-4 position-relative">
              <LLInput
                testid="phoneNumberStorePickupInfo"
                type="text"
                placeholder="Phone Number"
                id="phoneNumberStorePickupInfo"
                value={
                  storePickupInfoFormState[StorePickupInfoFormFields.PHONE_NO]
                }
                errorMsg={
                  storePickupInfoFormErrors?.[
                    StorePickupInfoFormFields.PHONE_NO
                  ]
                }
                onChange={(e) =>
                  handleFormChange(e, StorePickupInfoFormFields.PHONE_NO)
                }
                onBlur={(e) =>
                  onBlurHandler(StorePickupInfoFormFields.PHONE_NO, e)
                }
              />
              <div className="position-absolute w-auto input-icon">
                <LLPopover
                  text="Your phone number is required in case we need to contact you about your order."
                  position="top"
                >
                  <span className="ls-icon d-block icn-help-otl fs-6"></span>
                </LLPopover>
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center mt-ll-3 justify-content-end gap-ll-3">
            <LLButton
              type="submit"
              variation="primary"
              customClass="col-12 col-md-auto"
            >
              Submit Pickup Info
            </LLButton>
          </div>
        </form>
      </>
    </Conditional>
  );
};

export default BopisStorePickupInfo;
